import {
  DestroyRef,
  Injectable,
  WritableSignal,
  afterNextRender,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SearchRepository } from '@core/services/repositories/search.repository';
import { Ga4AnalyticsService } from '@core/services/analytics/ga4-analytics.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SearchBoxService {
  /*
            - inject
        */
  searchRepo = inject(SearchRepository);
  router = inject(Router);
  destroyRef = inject(DestroyRef);
  route = inject(ActivatedRoute);
  ga4Analytics = inject(Ga4AnalyticsService);
  isShabAdsCode = signal(false);
  noSearchResult = signal(false);
  
  public _openCalander: WritableSignal<boolean | any> = signal(null);
  public showFiltersSideNav: WritableSignal<boolean> = signal(false);
  public isSearch: WritableSignal<boolean> = signal(false);

  accommodation: WritableSignal<string> = signal('');
  toggleMobileSearchBox: WritableSignal<boolean> = signal(false);

  mobileSearchBoxIsOpen = new BehaviorSubject<boolean>(false);

  searchTrigger: WritableSignal<boolean> = signal(false);
  dateRange: Array<string> = [];
  residentType: 1 | 2 | 3 | 4 | 5 | 6 = 1;
  residentKeyForPayload?: string;
  residentId?: number;
  residentName = '';
  urlToRedirect = '';
  constructor() {
    this.accommodation.set(this.router.routerState.snapshot.url.split('/')[1]);
    afterNextRender(() => {
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.accommodation.set(
            this.router.routerState.snapshot.url.split('/')[1] == 'ecolodge' ? 'ecolodge' :
              this.router.routerState.snapshot.url.split('/')[1] == 'villa-mashhad' ? 'villa-mashhad' : '/'
          );
        }
      });
    });
  }
  getStartAndEndDate(): Array<string> {
    if (this.dateRange?.length)
      return [this.dateRange[0], this.dateRange[this.dateRange?.length - 1]];
    else {
      return ['', ''];
    }
  }

  triggerSearch(event: any, rerender = false, adCode: boolean) {
    this.isSearch.set(true);
    let startDate: string = '';
    let endDate: string = '';
    this.residentId = event.id;
    this.residentType = event.type;
    this.residentKeyForPayload = this.getResidentTypeName();
    this.urlToRedirect = event.link;
    this.residentName = event.name;
    if (event.startDate !== '' && event.endDate !== '') {
      startDate = event.startDate;
      endDate = event.endDate;
      this.dateRange = [event.startDate, event.endDate];
    } else {
      startDate = this.getStartAndEndDate()[0];
      endDate = this.getStartAndEndDate()[1];
    }
    const residentTypeKey = this.getResidentTypeName();
    let queryParams = {
      minPrice: null,
      maxPrice: null,
      labels: null,
      types: null,
      grades: null,
      reviews: null,
      date: startDate,
      endDate: endDate,
      type: String(this.residentType),
    };
    if (residentTypeKey) {
      (queryParams as any)[residentTypeKey] = String(this.residentId);
    }

    this.isShabAdsCode.set(event.isShabAdsCode);
    let isEcolodge = this.accommodation() == 'ecolodge' || this.accommodation() == 'villa-mashhad';
    // GA4 search:
    // شناسه/نام مقصد و عبارت جست‌وجو از autocomplete، تاریخ ورود/خروج از datepicker ارسال می‌شوند.
    // search_id داخل Ga4AnalyticsService ساخته و به همین event اضافه می‌شود.
    this.trackSearchEvent({ ...event, startDate, endDate });
    this.router
      .navigate(
        [
          isEcolodge && !event?.isShabAdsCode && !this.noSearchResult()
            ? '/ecolodge/' + event.name :
          this.noSearchResult() ? 'ecolodge/search' 

              : this.urlToRedirect,
        ],
        {
          queryParams: {
            search_box: this.residentName || null,
            refer: 'home',
            ...queryParams,
          },
          preserveFragment: false,
        }
      )

      
      .finally(() => {
        
        this.isSearch.set(false);
      });
  }

  /**
   * اعتبارسنجی مشترک فرم‌های جست‌وجوی Home/PLP/PDP و ثبت form_error بدون مقدار فیلد.
   * خروجی false یعنی caller باید submit/navigation را متوقف کند.
   */
  validateSearchForm(
    event: { id?: string | number; startDate?: string; endDate?: string; nop?: number },
    formName: string,
    pageType: 'php' | 'plp' | 'pdp',
    requireGuestCount = false
  ): boolean {
    const errors = [];
    if (event.id === undefined || event.id === null || event.id === '') {
      errors.push({ field_name: 'destination', error_type: 'required' });
    }
    if (!event.startDate) errors.push({ field_name: 'check_in_date', error_type: 'required' });
    if (!event.endDate) errors.push({ field_name: 'check_out_date', error_type: 'required' });
    if (requireGuestCount && (!event.nop || event.nop < 1)) {
      errors.push({ field_name: 'guest_count', error_type: 'required' });
    }

    if (errors.length) {
      this.ga4Analytics.trackFormErrors(formName, pageType, errors);
      return false;
    }
    return true;
  }

  /**
   * Central Search tracking entry point for components that navigate or refresh rooms directly.
   */
  trackSearchEvent(event: {
    id?: string | number;
    name?: string;
    startDate?: string;
    endDate?: string;
  }): string {
    return this.ga4Analytics.trackSearch({
      destination_id: event.id != null ? String(event.id) : undefined,
      destination_name: event.name,
      search_term: event.name,
      check_in_date: event.startDate,
      check_out_date: event.endDate,
    });
  }

  getResidentTypeName() {
    let residentTypeKey = '';
    if (this.residentType === 1) residentTypeKey = 'cityId';
    else if (this.residentType === 2) residentTypeKey = 'region';
    else if (this.residentType === 3) residentTypeKey = 'hid';
    else if (this.residentType === 4) residentTypeKey = 'district';
    else if (this.residentType === 5) residentTypeKey = 'state';
    return residentTypeKey;
  }

  getCurrentDate() {
    return this.searchRepo.getCurrentDate();
  }

  getHolidays(range: { StartDate: string; EndDate: string }) {
    return this.searchRepo.getHolidays(range);
  }
}
